// src/components/Login.js
import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { X, BookOpen, UserPlus, ShoppingCart, CheckCircle } from 'lucide-react';

function Login() {
    const { t } = useTranslation();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showVerificationLink, setShowVerificationLink] = useState(false);
    const [showHowToBookModal, setShowHowToBookModal] = useState(true);
    // First-login forced password change (admin-invited users with a temp password)
    const [newPasswordRequired, setNewPasswordRequired] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const { user, signIn, completeNewPassword } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    // Get the intended destination from location state, URL params, or default to '/'
    const searchParams = new URLSearchParams(location.search);
    const redirectParam = searchParams.get('redirect');
    const from = location.state?.from?.pathname || redirectParam || '/';

    // If user is already authenticated, redirect to home
    useEffect(() => {
        if (user) {
            console.log('User already authenticated, redirecting to:', from);
            navigate(from, { replace: true });
        }
    }, [user, navigate, from]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        console.log('Login attempt for username:', username);
        setLoading(true);
        setError(null);

        try {
            console.log('Calling signIn function');
            const result = await signIn(username, password);
            console.log('Login result:', result);

            // Invited staff with a temporary password must set a new one first.
            if (result && result.nextStep === 'NEW_PASSWORD_REQUIRED') {
                setNewPasswordRequired(true);
                setLoading(false);
                return;
            }

            // Redirect to the intended destination
            navigate(from, { replace: true });
        } catch (err) {
            console.error("Login failed:", err);
            
            // Handle different error types
            if (err.name === 'NotAuthorizedException') {
                setError(t('auth.login.errors.incorrectCredentials'));
            } else if (err.name === 'UserNotFoundException') {
                setError(t('auth.login.errors.userNotFound'));
            } else if (err.name === 'UserNotConfirmedException') {
                setError(t('auth.login.errors.userNotConfirmed'));
                setShowVerificationLink(true);
            } else {
                setError(t('auth.login.errors.loginFailed'));
            }
        } finally {
            setLoading(false);
        }
    };

    const handleNewPassword = async (e) => {
        e.preventDefault();
        setError(null);
        if (newPassword.length < 8) {
            setError(t('auth.login.errors.passwordTooShort', 'Password must be at least 8 characters.'));
            return;
        }
        if (newPassword !== confirmPassword) {
            setError(t('auth.login.errors.passwordsDoNotMatch', 'Passwords do not match.'));
            return;
        }
        setLoading(true);
        try {
            await completeNewPassword(newPassword);
            navigate(from, { replace: true });
        } catch (err) {
            console.error('Set new password failed:', err);
            setError(err.message || t('auth.login.errors.loginFailed'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            {/* How to Book Modal */}
            {showHowToBookModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="relative p-6 sm:p-8">
                            {/* Close button */}
                            <button
                                onClick={() => setShowHowToBookModal(false)}
                                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                                aria-label="Close modal"
                            >
                                <X size={24} />
                            </button>
                            
                            {/* Logo */}
                            <div className="flex justify-center mb-6">
                                <img 
                                    src="/ColaisteLogo.jpg" 
                                    alt="Coláiste Chorca Dhuibhne" 
                                    className="h-32 object-contain"
                                />
                            </div>
                            
                            {/* Title */}
                            <h2 className="text-3xl font-bold text-center text-blue-900 mb-8">
                                How to Book
                            </h2>
                            
                            {/* Steps */}
                            <div className="space-y-6">
                                {/* Step 1 */}
                                <div className="flex items-start space-x-4">
                                    <div className="flex-shrink-0">
                                        <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                                            <UserPlus className="text-white" size={20} />
                                        </div>
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="text-xl font-semibold text-gray-900 mb-2">
                                            Step 1 – Create Your Account
                                        </h3>
                                        <p className="text-gray-600">
                                            Use your email address and a password to set up your account. 
                                            Please keep your password safe!
                                        </p>
                                        <p className="text-gray-500 text-sm mt-2 italic">
                                            A verification code will be sent to your email. Please remember 
                                            to check your spam folder if you do not receive the code.
                                        </p>
                                    </div>
                                </div>
                                
                                {/* Step 2 */}
                                <div className="flex items-start space-x-4">
                                    <div className="flex-shrink-0">
                                        <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center">
                                            <BookOpen className="text-white" size={20} />
                                        </div>
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="text-xl font-semibold text-gray-900 mb-2">
                                            Step 2 – Add Your Students
                                        </h3>
                                        <p className="text-gray-600">
                                            Once your account is ready, log in and add your students' details.
                                        </p>
                                    </div>
                                </div>
                                
                                {/* Step 3 */}
                                <div className="flex items-start space-x-4">
                                    <div className="flex-shrink-0">
                                        <div className="w-12 h-12 bg-orange-600 rounded-full flex items-center justify-center">
                                            <ShoppingCart className="text-white" size={20} />
                                        </div>
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="text-xl font-semibold text-gray-900 mb-2">
                                            Step 3 – Book Your Course
                                        </h3>
                                        <p className="text-gray-600">
                                            To book a course, log in, choose your preferred course, and 
                                            secure your place by completing the course booking form, and 
                                            submitting the required deposit.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Continue button */}
                            <div className="mt-8 flex justify-center">
                                <button
                                    onClick={() => setShowHowToBookModal(false)}
                                    className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-lg transition-colors flex items-center space-x-2"
                                >
                                    <CheckCircle size={20} />
                                    <span>Got it, let's continue</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Login Form */}
            <div className="max-w-md mx-auto my-12">
                <div className="bg-white p-8 shadow-lg rounded-lg">
                    <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">{t('auth.login.title')}</h2>
                
                {error && (
                    <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
                        {error}
                        {showVerificationLink && (
                            <div className="mt-2">
                                <Link 
                                    to={`/signup?verify=true&username=${encodeURIComponent(username)}`}
                                    className="text-red-800 underline font-medium"
                                >
                                    Click here to resend verification code
                                </Link>
                            </div>
                        )}
                    </div>
                )}
                
                {newPasswordRequired ? (
                  <form onSubmit={handleNewPassword}>
                    <p className="mb-4 text-sm text-gray-600">
                        {t('auth.login.setNewPasswordPrompt', 'This is your first sign-in. Please set a new password to continue.')}
                    </p>
                    <div className="mb-4">
                        <label className="block text-gray-700 mb-2" htmlFor="newPassword">
                            {t('auth.login.newPassword', 'New password')}
                        </label>
                        <input
                            id="newPassword"
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className="border border-gray-300 p-3 w-full rounded"
                            required
                        />
                    </div>
                    <div className="mb-6">
                        <label className="block text-gray-700 mb-2" htmlFor="confirmPassword">
                            {t('auth.login.confirmPassword', 'Confirm new password')}
                        </label>
                        <input
                            id="confirmPassword"
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className="border border-gray-300 p-3 w-full rounded"
                            required
                        />
                    </div>
                    <button
                        type="submit"
                        className={`w-full py-3 px-4 rounded text-white font-medium ${loading ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'}`}
                        disabled={loading}
                    >
                        {loading ? t('auth.login.loggingIn') : t('auth.login.setPasswordButton', 'Set password & continue')}
                    </button>
                  </form>
                ) : (
                <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <label className="block text-gray-700 mb-2" htmlFor="username">
                            {t('auth.login.usernameOrEmail')}
                        </label>
                        <input
                            id="username"
                            type="text"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            className="border border-gray-300 p-3 w-full rounded"
                            required
                        />
                    </div>
                    
                    <div className="mb-6">
                        <label className="block text-gray-700 mb-2" htmlFor="password">
                            {t('auth.login.password')}
                        </label>
                        <input
                            id="password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="border border-gray-300 p-3 w-full rounded"
                            required
                        />
                    </div>
                    
                    <button
                        type="submit"
                        className={`w-full py-3 px-4 rounded text-white font-medium ${
                            loading ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'
                        }`}
                        disabled={loading}
                    >
                        {loading ? t('auth.login.loggingIn') : t('auth.login.loginButton')}
                    </button>
                </form>
                )}

                <div className="mt-6 text-center">
                    <p className="text-gray-600">
                        {t('auth.login.dontHaveAccount')}{' '}
                        <Link to="/signup" className="text-blue-600 hover:underline">
                            {t('auth.login.signUp')}
                        </Link>
                    </p>
                    <p className="mt-2">
                        <Link to="/forgot-password" className="text-blue-600 hover:underline">
                            {t('auth.login.forgotPassword')}
                        </Link>
                    </p>
                    <p className="mt-2">
                        <Link to="/signup?verify=true" className="text-blue-600 hover:underline">
                            Need to verify your email?
                        </Link>
                    </p>
                </div>
            </div>
        </div>
        </>
    );
}

export default Login;